import { useMemo } from 'react';

import { useMemberId } from '@trello/authentication';

import { useEligibleWorkspacesForProvisioningQuery } from './EligibleWorkspacesForProvisioningQuery.generated';

interface EligibleWorkspaceOption {
  id: string;
  displayName: string;
}

interface HookResult {
  options: EligibleWorkspaceOption[];
  initialValue?: string;
}

/**
 * Derives eligible workspace options for the provisioning and linking flow.
 *
 * Users with multiple eligible workspaces are presented with a workspace
 * selector in the site creation screen, indicating which Trello workspace
 * should be linked to the created JWM site.
 *
 * This hook populates the values for the workspace selector in CFFE, which are
 * represented as `id` and `displayName`. When triggering this flow from a
 * workspace-aware context (e.g. within a board), the initial workspace value
 * can be pre-selected as an argument for this method. Because it relies on
 * context, the current workspace ID should be stable; if it changes often,
 * this logic could be hoisted elsewhere.
 */
export const useEligibleWorkspacesForProvisioning = (
  currentWorkspaceId?: string,
): HookResult => {
  const memberId = useMemberId();
  const { data } = useEligibleWorkspacesForProvisioningQuery({
    fetchPolicy: 'cache-only',
    variables: { memberId },
    waitOn: ['MemberHeader'],
  });
  const organizations = data?.member?.organizations;

  return useMemo(() => {
    if (!organizations?.length) {
      return { options: [] };
    }

    const options = organizations
      // At present, workspaces are only eligible if they are completely free.
      .filter(({ idEnterprise, products }) => !idEnterprise && !products.length)
      .map(({ id, displayName }) => ({ id, displayName }));

    const result: HookResult = { options };

    if (
      currentWorkspaceId &&
      options.find(({ id }) => id === currentWorkspaceId)
    ) {
      result.initialValue = currentWorkspaceId;
    }

    return result;
  }, [currentWorkspaceId, organizations]);
};
